import { getCustomerAuthFromCookies } from '@bulknutrients/bulk-shared-lib/lib/api/auth';
import { getCustomerId } from '@bulknutrients/bulk-shared-lib/lib/api/customer';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button, ButtonType } from '@components/Button';
import { Container } from '@components/Container';
import { Input } from '@components/FormComponents';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { log } from '@lib/logs';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { type ReactElement, type ReactNode, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

export default function ForgottenPasswordPage(): ReactElement {
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState<ReactNode>();
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const router = useRouter();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = async (e: { email: string }) => {
    setSubmitting(true);
    try {
      const response = await fetch('api/customer/reset-password-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: e.email }),
      });

      if (response.status === 200) {
        //   done it
        setSuccess(true);
        setTimeout(() => {
          router.push('/reset-password');
        }, 5000);
      }
      if (response.status === 400) {
        setSuccess(true);
        setTimeout(() => {
          router.push('/reset-password');
        }, 5000);
      }
      if (response.status === 401) {
        //   no account found
        setError(true);
        setErrorText(
          <Paragraph>
            Oh, looks like you are being a little bit cheeky there. That
            obviously isn't going to work.
          </Paragraph>
        );
      }
      if (response.status === 429) {
        //   no account found
        setError(true);
        setErrorText(
          <Paragraph>
            It seems like a lot of requests are coming in from you, so we've had
            to send you to the naughty corner on a timeout!
          </Paragraph>
        );
      }
      if (response.status === 418) {
        //   no account found
        setError(true);
        setErrorText(
          <Paragraph>
            You already asked for a password reset! You need to check your email
            and follow the instructions there, or else{' '}
            <TextLink href="/contact-us">contact Customer Service</TextLink>.
          </Paragraph>
        );
      }
      if (response.status === 500) {
        //   other error, non bulk account 🙄
        setError(true);
        setErrorText(
          <Paragraph>
            Uh oh, something unexpectedly went wrong. If you continue having
            issues, please contact Customer Service.
          </Paragraph>
        );
      }
    } catch (err) {
      log({ error: err });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SuccessAlert
        message="Thanks! If the email address you submitted is valid, you'll receive an email with instructions on how to reset your password within a few minutes.
        Sit tight, we're redirecting you back to the login in page now."
        setShow={setSuccess}
        show={success}
        title="Request Received"
      />
      <ErrorAlert setShow={setError} show={error}>
        {errorText}
      </ErrorAlert>
      <Head>
        <title>Bulk Nutrients' forgot password form | Bulk Nutrients</title>
        <meta
          content="Bulk Nutrients' forgot password form | Bulk Nutrients"
          name="title"
        />
        <meta
          content="Forgot your password to log into the Bulk Nutrients online supplement store? We're to help! Just fill in the details and we'll have you back on track!
          "
          name="description"
        />
        <link
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/forgot-password`}
          rel="canonical"
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Login', path: '/login' },
            { title: 'Reset Password', path: '/forgot-password' },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <div className="my-0 max-w-narrow-page justify-self-center">
          <h1>Reset Password</h1>
          <Paragraph>
            It's ok, we all forget passwords from time to time 😉
          </Paragraph>
          <Paragraph>
            Simply pop in your email address below and we’ll send you an email
            to reset your password.
          </Paragraph>
          <Paragraph>Haven't received your password reset email?</Paragraph>
          <Paragraph>
            Remember to check your spam folder because sometimes the password
            reset email hides in there. Otherwise you might have checked out as
            a guest last time, but you can quickly{' '}
            <TextLink href="/create-account">create an account here</TextLink>.
          </Paragraph>

          <FormProvider {...rhfMethods}>
            <form
              className="flex flex-col items-start gap-2.5 pb-5"
              data-testid="pwd-reset-form"
              onSubmit={rhfMethods.handleSubmit(handleSubmit)}
            >
              <Input
                className="min-w-auto w-full sm:w-[400px]"
                label="Email Address"
                name="email"
                placeholder="email@example.com"
                required
                type="email"
              />
              <Button
                buttonStyle="primary"
                disabled={submitting}
                type={ButtonType.submit}
              >
                Reset Password
              </Button>
            </form>
          </FormProvider>
        </div>
      </Container>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  // Get customer id if available
  const { cookies } = req;
  const customerAuth = getCustomerAuthFromCookies(cookies);
  const customerId = customerAuth && (await getCustomerId(customerAuth));

  // If customer, redirect to account order history
  if (customerId) {
    return {
      redirect: {
        destination: '/account/orders',
        permanent: false,
      },
    };
  }

  // return empty props
  return { props: {} };
};

ForgottenPasswordPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
